// Announcements: the standing prompts boxes play, the ads, and how often an ad plays.
import React, { useState } from 'react';
import { toast } from 'sonner';
import moment from 'moment';
import { CheckCircle2, Pencil, Play, Power, PowerOff, RefreshCw, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import {
  promptAudioUrl,
  useVoicePromptDeleteMutation,
  useVoicePromptUpdateMutation,
  useVoicePromptUploadMutation,
  useVoicePromptsQuery,
  useVoiceRefresh,
  useVoiceSettingsQuery,
  useVoiceSettingsUpdateMutation,
} from '@/client/api/voice/voice.query';
import { PromptPatch, SlotInfo, VoicePrompt } from '@/client/types/voice';
import { Card, EmptyRow, FormDialog, FormField, INPUT_CLASS, PageShell, Th, fmtNum } from '../Cloud/cloudUi';
import {
  AccessDenied,
  AudioPlayer,
  ErrorNotice,
  FetchingChip,
  Kebab,
  KebabItem,
  LoadingNote,
  VOICE_BASE,
  fmtBytes,
  fmtMs,
  fmtShort,
  useVoicePerms,
} from './voiceUi';

const MAX_BYTES = 5 * 1024 * 1024;

const FALLBACK_SLOTS: SlotInfo[] = [
  { slot: 'buyer_welcome', label: 'Welcome' },
  { slot: 'recording_notice', label: 'Recording notice', required: true },
  { slot: 'seller_notice', label: 'Seller notice' },
  { slot: 'hold', label: 'Hold' },
  { slot: 'unavailable', label: 'Unavailable' },
  { slot: 'system_failure', label: 'System failure' },
];

const SLOT_HELP: Record<string, string> = {
  buyer_welcome: 'First thing the buyer hears when they call the number.',
  recording_notice: 'Tells both people the call is recorded. Always played; cannot be removed.',
  seller_notice: 'Played to the seller before they are connected.',
  hold: 'Played while the buyer waits.',
  unavailable: 'Played when the seller cannot be reached.',
  system_failure: 'Played when something goes wrong on our side.',
};

/** Length of an audio file, read in the browser. Undefined when the browser cannot decode it. */
const audioDurationMs = (file: File): Promise<number | undefined> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const a = new Audio();
    const done = (v?: number) => {
      URL.revokeObjectURL(url);
      resolve(v);
    };
    a.preload = 'metadata';
    a.onloadedmetadata = () => done(Number.isFinite(a.duration) ? Math.round(a.duration * 1000) : undefined);
    a.onerror = () => done(undefined);
    a.src = url;
  });

const dayInput = (iso?: string | null) => (iso ? moment(iso).format('YYYY-MM-DD') : '');
const windowText = (p: VoicePrompt) =>
  p.starts_at || p.ends_at
    ? `${p.starts_at ? moment(p.starts_at).format('D MMM YYYY') : 'now'} to ${p.ends_at ? moment(p.ends_at).format('D MMM YYYY') : 'no end'}`
    : 'Always';

/* ---------- dialogs ---------- */

type Dialog =
  | { kind: 'upload'; slot: string; label: string }
  | { kind: 'edit'; prompt: VoicePrompt }
  | { kind: 'active'; prompt: VoicePrompt; active: boolean }
  | { kind: 'delete'; prompt: VoicePrompt };

const PromptFormDialog: React.FC<{
  mode: 'upload' | 'edit';
  slot: string;
  label: string;
  prompt?: VoicePrompt;
  onClose: () => void;
}> = ({ mode, slot, label, prompt, onClose }) => {
  const upload = useVoicePromptUploadMutation();
  const update = useVoicePromptUpdateMutation();
  const isAd = slot === 'ad';
  const [file, setFile] = useState<File | null>(null);
  const [durationMs, setDurationMs] = useState<number | undefined>();
  const [name, setName] = useState(prompt?.name ?? '');
  const [transcript, setTranscript] = useState(prompt?.transcript ?? '');
  const [activate, setActivate] = useState(true);
  const [weight, setWeight] = useState(String(prompt?.weight ?? 1));
  const [starts, setStarts] = useState(dayInput(prompt?.starts_at));
  const [ends, setEnds] = useState(dayInput(prompt?.ends_at));
  const tooBig = !!file && file.size > MAX_BYTES;
  const w = Number(weight);
  const valid =
    name.trim().length > 0 &&
    (mode === 'edit' || (!!file && !tooBig)) &&
    (!isAd || (Number.isInteger(w) && w >= 1 && w <= 100));
  const busy = upload.isPending || update.isPending;
  const startIso = starts ? moment(starts).startOf('day').toISOString() : undefined;
  const endIso = ends ? moment(ends).endOf('day').toISOString() : undefined;

  const pick = async (f: File | null) => {
    setFile(f);
    setDurationMs(undefined);
    if (f) {
      setDurationMs(await audioDurationMs(f));
      if (!name) setName(f.name.replace(/\.[^.]+$/, ''));
    }
  };

  return (
    <FormDialog
      title={mode === 'upload' ? (isAd ? 'Upload an ad' : `Upload a new "${label}" version`) : `Edit "${prompt?.name}"`}
      description={mode === 'upload' ? 'Audio up to 5 MB. Boxes convert it to phone quality.' : undefined}
      confirmLabel={mode === 'upload' ? 'Upload' : 'Save'}
      valid={valid}
      busy={busy}
      error={upload.error ?? update.error}
      onClose={onClose}
      onConfirm={(reason) => {
        if (mode === 'upload' && file) {
          upload.mutate(
            {
              reason,
              upload: {
                file,
                slot,
                name: name.trim(),
                transcript: transcript.trim() || undefined,
                duration_ms: durationMs,
                weight: isAd ? w : undefined,
                starts_at: isAd ? startIso : undefined,
                ends_at: isAd ? endIso : undefined,
                activate,
              },
            },
            { onSuccess: () => { toast.success('Audio uploaded'); onClose(); } },
          );
        } else if (prompt) {
          const patch: PromptPatch = { name: name.trim(), transcript: transcript.trim() };
          if (isAd) Object.assign(patch, { weight: w, starts_at: startIso ?? null, ends_at: endIso ?? null });
          update.mutate(
            { promptId: prompt.prompt_id, reason, patch },
            { onSuccess: () => { toast.success('Saved'); onClose(); } },
          );
        }
      }}
    >
      {mode === 'upload' && (
        <FormField label="Audio file" hint={file ? `${fmtBytes(file.size)}${durationMs != null ? `, ${fmtMs(durationMs)}` : ''}` : 'MP3, WAV, M4A, AAC or OGG'}>
          <input
            type="file"
            accept="audio/*"
            className="mt-1 block w-full text-sm"
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
          />
        </FormField>
      )}
      {tooBig && <p className="text-sm text-red-600">This file is {fmtBytes(file!.size)}. The limit is 5 MB.</p>}
      <FormField label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} className={`${INPUT_CLASS} mt-1 w-full`} />
      </FormField>
      <FormField label="Transcript" hint="What the recording says, for people reading this page.">
        <textarea
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-blue-400"
        />
      </FormField>
      {isAd && (
        <div className="grid grid-cols-3 gap-3">
          <FormField label="Weight (1-100)" hint="Higher plays more often.">
            <input type="number" min={1} max={100} value={weight} onChange={(e) => setWeight(e.target.value)} className={`${INPUT_CLASS} mt-1 w-full`} />
          </FormField>
          <FormField label="Starts">
            <input type="date" value={starts} onChange={(e) => setStarts(e.target.value)} className={`${INPUT_CLASS} mt-1 w-full`} />
          </FormField>
          <FormField label="Ends">
            <input type="date" value={ends} onChange={(e) => setEnds(e.target.value)} className={`${INPUT_CLASS} mt-1 w-full`} />
          </FormField>
        </div>
      )}
      {mode === 'upload' && (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={activate} onChange={(e) => setActivate(e.target.checked)} />
          Make active {isAd ? '' : `(replaces the current "${label}")`}
        </label>
      )}
    </FormDialog>
  );
};

const ActiveDialog: React.FC<{ prompt: VoicePrompt; active: boolean; onClose: () => void }> = ({ prompt, active, onClose }) => {
  const m = useVoicePromptUpdateMutation();
  const isAd = prompt.slot === 'ad';
  return (
    <FormDialog
      title={active ? `Make "${prompt.name}" active` : `Turn off "${prompt.name}"`}
      description={
        active
          ? isAd
            ? 'This ad can be played on calls again.'
            : 'Boxes start playing this version within a minute. The version now active is switched off.'
          : 'This ad stops playing on new calls.'
      }
      confirmLabel={active ? 'Make active' : 'Turn off'}
      busy={m.isPending}
      error={m.error}
      onClose={onClose}
      onConfirm={(reason) =>
        m.mutate({ promptId: prompt.prompt_id, reason, patch: { active } }, { onSuccess: () => { toast.success(active ? 'Now active' : 'Turned off'); onClose(); } })
      }
    />
  );
};

const DeleteDialog: React.FC<{ prompt: VoicePrompt; onClose: () => void }> = ({ prompt, onClose }) => {
  const m = useVoicePromptDeleteMutation();
  return (
    <FormDialog
      title={`Delete "${prompt.name}"`}
      description="The audio is removed for good. Calls already made keep their record."
      confirmLabel="Delete"
      destructive
      busy={m.isPending}
      error={m.error}
      onClose={onClose}
      onConfirm={(reason) =>
        m.mutate({ promptId: prompt.prompt_id, reason }, { onSuccess: () => { toast.success('Deleted'); onClose(); } })
      }
    />
  );
};

/* ---------- standing slots ---------- */

const SlotCard: React.FC<{
  info: SlotInfo;
  prompts: VoicePrompt[];
  canManage: boolean;
  open: (d: Dialog) => void;
}> = ({ info, prompts, canManage, open }) => {
  const mine = prompts.filter((p) => p.slot === info.slot);
  const active = mine.find((p) => p.active || p.prompt_id === info.active_prompt_id);
  const others = mine.filter((p) => p !== active);
  return (
    <Card
      title={info.label}
      note={SLOT_HELP[info.slot]}
      action={
        <div className="flex items-center gap-2">
          {info.required && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">Required</span>}
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => open({ kind: 'upload', slot: info.slot, label: info.label })}>
              <Upload className="mr-2 h-3.5 w-3.5" /> Upload new version
            </Button>
          )}
        </div>
      }
    >
      {active ? (
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 font-medium text-gray-900">
                <CheckCircle2 className="h-4 w-4 text-green-600" /> {active.name}
              </p>
              <p className="text-xs text-gray-500">{fmtMs(active.duration_ms)} · {fmtBytes(active.size_bytes)} · uploaded {fmtShort(active.created_at)}</p>
            </div>
            {canManage && (
              <Kebab
                name={active.name}
                items={[{ label: 'Edit name and transcript', icon: <Pencil className="h-4 w-4" />, onSelect: () => open({ kind: 'edit', prompt: active }) }]}
              />
            )}
          </div>
          {active.transcript && <p className="text-sm text-gray-700">&ldquo;{active.transcript}&rdquo;</p>}
          <AudioPlayer src={promptAudioUrl(active.prompt_id)} />
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          No upload is active: boxes play their own built-in sound for this.
        </p>
      )}
      {others.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <p className="mb-1 text-xs font-medium text-gray-500">Other versions</p>
          <ul className="divide-y divide-gray-50">
            {others.map((p) => {
              const items: KebabItem[] = [
                { label: 'Make active', icon: <Play className="h-4 w-4" />, onSelect: () => open({ kind: 'active', prompt: p, active: true }) },
                { label: 'Edit name and transcript', icon: <Pencil className="h-4 w-4" />, onSelect: () => open({ kind: 'edit', prompt: p }) },
                { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, danger: true, onSelect: () => open({ kind: 'delete', prompt: p }) },
              ];
              return (
                <li key={p.prompt_id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-gray-900">{p.name}</p>
                    <p className="text-xs text-gray-500">{fmtMs(p.duration_ms)} · uploaded {fmtShort(p.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <AudioPlayer src={promptAudioUrl(p.prompt_id)} className="max-w-[14rem]" />
                    {canManage && <Kebab name={p.name} items={items} />}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
};

/* ---------- ad frequency ---------- */

const AdFrequency: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const q = useVoiceSettingsQuery();
  const m = useVoiceSettingsUpdateMutation();
  const [value, setValue] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const s = q.data;
  const shown = value ?? String(s?.ad_frequency_percent ?? '');
  const n = Number(shown);
  const valid = shown !== '' && Number.isInteger(n) && n >= 0 && n <= 100;
  const dirty = !!s && value !== null && n !== s.ad_frequency_percent;
  return (
    <Card title="Ad frequency" note="Percent of calls that hear an ad">
      <p className="mb-3 text-sm text-gray-600">
        Each new call plays an ad with this chance, picked from the active ads whose dates include today,
        favouring higher weights. 0 turns ads off; 100 plays one on every call.
      </p>
      {q.isLoading ? (
        <LoadingNote>Loading the ad frequency setting.</LoadingNote>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="number"
            min={0}
            max={100}
            disabled={!canManage}
            value={shown}
            onChange={(e) => setValue(e.target.value)}
            className={`${INPUT_CLASS} w-24`}
          />
          <span className="text-sm text-gray-500">%</span>
          {canManage && (
            <Button size="sm" disabled={!dirty || !valid} onClick={() => setConfirm(true)}>
              Save
            </Button>
          )}
        </div>
      )}
      {confirm && s && (
        <FormDialog
          title="Change ad frequency"
          description={`From ${s.ad_frequency_percent}% to ${n}% of calls.`}
          confirmLabel="Save"
          busy={m.isPending}
          error={m.error}
          onClose={() => setConfirm(false)}
          onConfirm={(reason) =>
            m.mutate(
              { reason, settings: { hold_seconds: s.hold_seconds, max_call_minutes: s.max_call_minutes, ad_frequency_percent: n } },
              { onSuccess: () => { toast.success('Ad frequency saved'); setValue(null); setConfirm(false); } },
            )
          }
        />
      )}
    </Card>
  );
};

/* ---------- page ---------- */

const VoiceAnnouncementsPage: React.FC = () => {
  useBreadcrumbs([{ label: 'Voice calling', href: VOICE_BASE }, { label: 'Announcements' }]);
  const perms = useVoicePerms();
  const refresh = useVoiceRefresh();
  const q = useVoicePromptsQuery();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const close = () => setDialog(null);

  if (perms.loading || !perms.canView) {
    return (
      <PageShell title="Announcements" description="">
        {perms.loading ? <Skeleton className="h-64 w-full" /> : <AccessDenied />}
      </PageShell>
    );
  }

  const prompts = q.data?.prompts ?? [];
  const slots = (q.data?.slots?.length ? q.data.slots : FALLBACK_SLOTS).filter((s) => s.slot !== 'ad');
  const ads = prompts.filter((p) => p.slot === 'ad');

  return (
    <PageShell
      title="Announcements"
      description="What callers and sellers hear before a call connects, plus the ads that can play first."
      actions={
        <div className="flex items-center gap-2">
          <FetchingChip />
          <Button variant="outline" size="sm" onClick={() => refresh()}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      }
    >
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading ? (
        <>
          <LoadingNote>Loading announcements and ads.</LoadingNote>
          <Skeleton className="h-40 w-full" />
        </>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            {slots.map((s) => (
              <SlotCard key={s.slot} info={s} prompts={prompts} canManage={perms.canManage} open={setDialog} />
            ))}
          </div>

          <Card
            title="Ads"
            note="Played before the welcome, on a share of calls"
            flush
            action={
              perms.canManage && (
                <Button size="sm" variant="outline" onClick={() => setDialog({ kind: 'upload', slot: 'ad', label: 'Ad' })}>
                  <Upload className="mr-2 h-3.5 w-3.5" /> Upload ad
                </Button>
              )
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500">
                  <tr>
                    <Th>Name</Th>
                    <Th>Status</Th>
                    <Th right>Weight</Th>
                    <Th>Window</Th>
                    <Th right>Plays, 30 days</Th>
                    <Th>Listen</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {ads.length === 0 ? (
                    <EmptyRow cols={7}>No ads uploaded.</EmptyRow>
                  ) : (
                    ads.map((p) => (
                      <tr key={p.prompt_id} className="cursor-pointer hover:bg-gray-50" onClick={() => perms.canManage && setDialog({ kind: 'edit', prompt: p })}>
                        <td className="px-4 py-2">
                          <p className="text-gray-900">{p.name}</p>
                          {p.transcript && <p className="max-w-xs truncate text-xs text-gray-500">{p.transcript}</p>}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${p.active ? 'bg-green-50 text-green-700 ring-green-200' : 'bg-slate-100 text-slate-600 ring-slate-200'}`}>
                            {p.active ? 'Active' : 'Off'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right text-gray-700">{p.weight ?? 1}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-gray-700">{windowText(p)}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{fmtNum(p.plays_30d ?? 0)}</td>
                        <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                          <AudioPlayer src={promptAudioUrl(p.prompt_id)} className="max-w-[14rem]" />
                        </td>
                        <td className="px-4 py-2 text-right">
                          {perms.canManage && (
                            <Kebab
                              name={p.name}
                              items={[
                                { label: 'Edit', icon: <Pencil className="h-4 w-4" />, onSelect: () => setDialog({ kind: 'edit', prompt: p }) },
                                p.active
                                  ? { label: 'Turn off', icon: <PowerOff className="h-4 w-4" />, onSelect: () => setDialog({ kind: 'active', prompt: p, active: false }) }
                                  : { label: 'Make active', icon: <Power className="h-4 w-4" />, onSelect: () => setDialog({ kind: 'active', prompt: p, active: true }) },
                                { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, danger: true, hidden: p.active, onSelect: () => setDialog({ kind: 'delete', prompt: p }) },
                              ]}
                            />
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <AdFrequency canManage={perms.canManage} />
        </>
      )}

      {dialog?.kind === 'upload' && <PromptFormDialog mode="upload" slot={dialog.slot} label={dialog.label} onClose={close} />}
      {dialog?.kind === 'edit' && <PromptFormDialog mode="edit" slot={dialog.prompt.slot} label={dialog.prompt.name} prompt={dialog.prompt} onClose={close} />}
      {dialog?.kind === 'active' && <ActiveDialog prompt={dialog.prompt} active={dialog.active} onClose={close} />}
      {dialog?.kind === 'delete' && <DeleteDialog prompt={dialog.prompt} onClose={close} />}
    </PageShell>
  );
};

export default VoiceAnnouncementsPage;
